import { HTTP as http } from '../http-common';

// All endpoints under /api/task/* + /api/work-calendar/week.
// Backend wraps every response as { status: 'success'|'error', data, error } —
// the http-common interceptor unwraps response.data, so consumers see that
// shape directly.
export default {
  // ===== Task =====
  list(params) {
    return http.get('/api/task', { params });
  },
  kanban(params) {
    return http.get('/api/task/kanban', { params });
  },
  get(id) {
    return http.get(`/api/task/${id}`);
  },
  create(body) {
    return http.post('/api/task', body);
  },
  quickCreate(body) {
    return http.post('/api/task/quick', body);
  },
  update(id, body) {
    return http.patch(`/api/task/${id}`, body);
  },
  changeStatus(id, body) {
    return http.patch(`/api/task/${id}/status`, body);
  },
  delete(id) {
    return http.delete(`/api/task/${id}`);
  },
  history(id) {
    return http.get(`/api/task/${id}/history`);
  },

  // ===== Comments =====
  listComments(id) {
    return http.get(`/api/task/${id}/comments`);
  },
  addComment(id, body) {
    return http.post(`/api/task/${id}/comments`, body);
  },

  // ===== Links =====
  listLinks(id) {
    return http.get(`/api/task/${id}/links`);
  },
  createLink(id, body) {
    return http.post(`/api/task/${id}/links`, body);
  },
  deleteLink(linkId) {
    return http.delete(`/api/task/links/${linkId}`);
  },

  // ===== Attachments =====
  listAttachments(id) {
    return http.get(`/api/task/${id}/attachments`);
  },
  // The spec declares `files` as query (multipart), so server parses files
  // from form-data; we send as multipart/form-data with each file in `files`.
  uploadAttachments(id, files) {
    const fd = new FormData();
    (files || []).forEach((f) => fd.append('files', f));
    return http.post(`/api/task/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment(attachmentId) {
    return http.delete(`/api/task/attachments/${attachmentId}`);
  },

  // ===== Reminders =====
  listReminders(id) {
    return http.get(`/api/task/${id}/reminder`);
  },
  addReminder(id, body) {
    return http.post(`/api/task/${id}/reminder`, body);
  },
  deleteReminder(reminderId) {
    return http.delete(`/api/task/reminder/${reminderId}`);
  },

  // ===== Labels =====
  listLabels() {
    return http.get('/api/task/label');
  },
  createLabel(body) {
    return http.post('/api/task/label', body);
  },
  updateLabel(id, body) {
    return http.patch(`/api/task/label/${id}`, body);
  },
  deleteLabel(id) {
    return http.delete(`/api/task/label/${id}`);
  },

  // ===== Fields =====
  listFields() {
    return http.get('/api/task/field');
  },
  listAvailableFields() {
    return http.get('/api/task/field/available');
  },
  createField(body) {
    return http.post('/api/task/field', body);
  },
  updateField(id, body) {
    return http.patch(`/api/task/field/${id}`, body);
  },
  deleteField(id) {
    return http.delete(`/api/task/field/${id}`);
  },
  reorderFields(body) {
    return http.post('/api/task/field/reorder', body);
  },
  listFieldValues(taskId) {
    return http.get(`/api/task/field/task/${taskId}/values`);
  },
  setFieldValue(taskId, fieldId, body) {
    return http.put(
      `/api/task/field/task/${taskId}/values/${fieldId}`,
      body
    );
  },
  deleteFieldValue(taskId, fieldId) {
    return http.delete(
      `/api/task/field/task/${taskId}/values/${fieldId}`
    );
  },

  // ===== Columns =====
  listColumns() {
    return http.get('/api/task/column');
  },
  createColumn(body) {
    return http.post('/api/task/column', body);
  },
  updateColumn(id, body) {
    return http.patch(`/api/task/column/${id}`, body);
  },
  deleteColumn(id) {
    return http.delete(`/api/task/column/${id}`);
  },

  // ===== Automation =====
  listAutomation() {
    return http.get('/api/task/automation');
  },
  createAutomation(body) {
    return http.post('/api/task/automation', body);
  },
  previewAutomation(body) {
    return http.post('/api/task/automation/preview', body);
  },
  getAutomation(id) {
    return http.get(`/api/task/automation/${id}`);
  },
  updateAutomation(id, body) {
    return http.patch(`/api/task/automation/${id}`, body);
  },
  deleteAutomation(id) {
    return http.delete(`/api/task/automation/${id}`);
  },
  runsByRule(id) {
    return http.get(`/api/task/automation/${id}/runs`);
  },
  runsByTask(taskId) {
    return http.get(`/api/task/automation/runs/task/${taskId}`);
  },

  // ===== Work Calendar =====
  calendarWeek(from, to) {
    return http.get('/api/work-calendar/week', { params: { from, to } });
  },
};

// ===== Mappers between UI/mock format and API format =====
//
// API uses: statusKey, assigneeUsername, priority enum LOW|MEDIUM|HIGH|URGENT,
// dueAt epoch ms, labelIds[]. Our UI uses: status, assignee (id), priority
// low|mid|high, due 'yyyy-mm-dd', label (single id).
// Round-trip helpers keep the rest of the UI untouched.

export const PRIORITY_API_TO_UI = {
  LOW: 'low',
  MEDIUM: 'mid',
  HIGH: 'high',
  URGENT: 'high',
};
export const PRIORITY_UI_TO_API = {
  low: 'LOW',
  mid: 'MEDIUM',
  high: 'HIGH',
};

// Epoch ms → 'yyyy-MM-dd HH:mm' string used by the date+time picker.
export function dueAtToString(epochMs) {
  if (epochMs == null) return '';
  const d = new Date(Number(epochMs));
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${dd} ${hh}:${mi}`;
}
// Accepts either 'yyyy-MM-dd' (legacy) or 'yyyy-MM-dd HH:mm' (new). Returns
// epoch ms, or null when the input is empty/invalid.
export function stringToDueAt(s) {
  if (!s) return null;
  const str = String(s).trim();
  // If only the date part is present, default time to 00:00 (preserves the
  // old behaviour for tasks that pre-date the time picker).
  const isoLike = /^\d{4}-\d{2}-\d{2}$/.test(str)
    ? str + 'T00:00:00'
    : str.replace(' ', 'T') + ':00';
  const d = new Date(isoLike);
  return isNaN(d.getTime()) ? null : d.getTime();
}

// Resolve a list of label IDs from whichever shape the BE shipped.
function pickLabelIds(t) {
  if (Array.isArray(t.labels) && t.labels.length) {
    return t.labels.map((l) => (typeof l === 'object' ? l.id : l)).filter((v) => v != null);
  }
  if (Array.isArray(t.labelIds) && t.labelIds.length) return t.labelIds.slice();
  return [];
}

export function normalizeTaskFromApi(t) {
  if (!t) return t;
  return {
    id: t.id,
    code: t.code || ('CV-' + t.id),
    title: t.title || '',
    description: t.description || '',
    status: t.statusKey || 'TODO',
    // BE now supports multi-assignee: `assigneeUsernames` = full list,
    // `assigneeUsername` = primary (element 0). FE keeps `assignee` as an
    // array so the UI can render every avatar; falls back to a single-
    // element list for legacy responses.
    assignee: Array.isArray(t.assigneeUsernames) && t.assigneeUsernames.length
      ? t.assigneeUsernames.slice()
      : (t.assigneeUsername ? [t.assigneeUsername] : []),
    reporter: t.reporterUsername || null,
    priority: PRIORITY_API_TO_UI[t.priority] || 'mid',
    due: dueAtToString(t.dueAt),
    // Labels — BE may ship either
    //   labels: [{id, name, color}, ...]  (new)
    // or
    //   labelIds: [12, 14]                (legacy)
    // We normalise to an array of IDs for `labels` and pull the first id
    // into `label` (single-select fallback used by the kanban card).
    label: pickLabelIds(t)[0] ?? null,
    labels: pickLabelIds(t),
    team: t.teamId || null,
    // BE response now ships:
    //   contactIds: [12670254, 12670255]
    //   contacts:   [{id, name, phone}, ...]
    // Prefer `contacts` for the id list (its element order matches what
    // the user saw in the UI) and only fall back to contactIds / legacy
    // single contactId when contacts is absent.
    customer: Array.isArray(t.contacts) && t.contacts.length
      ? t.contacts.map((c) => c.id)
      : (Array.isArray(t.contactIds) && t.contactIds.length
        ? t.contactIds
        : (t.contactId != null ? [t.contactId] : [])),
    // Keep the full contact ref list — labels render without an extra
    // GET /api/contact/get-by-id round-trip.
    contacts: Array.isArray(t.contacts) ? t.contacts : null,
    reminder: t.reminderText || '',
    watching: t.watching || 1,
    createdAt: t.createAt || Date.now(),
    customFieldValues: t.customFieldValues || {},
    _api: true,
  };
}

// TaskCreateRequest body — matches the backend schema exactly:
//   { title, description, statusKey, assigneeUsername, dueAt(Long epoch ms),
//     priority(LOW|MEDIUM|HIGH|URGENT), teamId, contactId, labelIds[],
//     reminder: { offsetValue(Long), offsetUnit(MINUTE|HOUR|DAY) } | null }
export function denormalizeTaskForApi(t) {
  if (!t) return t;
  const body = {
    title: t.title || '',
    description: t.description || '',
    statusKey: t.status || 'TODO',
    // Multi-assignee: BE prefers `assigneeUsernames` (full list). Send
    // both fields — modern BE ignores `assigneeUsername`, legacy BE only
    // reads it. First element = primary assignee.
    ...assigneeUsernamesToBody(t.assignee),
    priority: PRIORITY_UI_TO_API[t.priority] || 'MEDIUM',
    dueAt: stringToDueAt(t.due),
    labelIds: toInt64Array(t.labels && t.labels.length ? t.labels : (t.label ? [t.label] : [])),
    teamId: toInt64(t.team),
    ...customerIdsToBody(t.customer),
  };
  const r = parseReminderInput(t);
  if (r) body.reminder = r;
  return body;
}

// Build ReminderInput from a task object. Accepts either:
//   - t.reminderInput = { offsetValue, offsetUnit } (preferred, new shape)
//   - t.reminder = "30 m" / "2 h" / "1 d"          (legacy string)
// Returns null when nothing is set / qty <= 0.
export function parseReminderInput(t) {
  if (!t) return null;
  if (t.reminderInput
      && Number(t.reminderInput.offsetValue) > 0
      && t.reminderInput.offsetUnit) {
    return {
      offsetValue: Number(t.reminderInput.offsetValue),
      offsetUnit: String(t.reminderInput.offsetUnit).toUpperCase(),
    };
  }
  if (t.reminder) {
    const m = String(t.reminder).match(/^\s*(\d+)\s*(m|h|d|MINUTE|HOUR|DAY)?\s*$/i);
    if (m && Number(m[1]) > 0) {
      const unitMap = { m: 'MINUTE', h: 'HOUR', d: 'DAY' };
      const raw = (m[2] || 'm').toLowerCase();
      const unit = unitMap[raw] || (m[2] || 'MINUTE').toUpperCase();
      return { offsetValue: Number(m[1]), offsetUnit: unit };
    }
  }
  return null;
}

// TaskUpdateRequest body (NO statusKey — status changes go through
// PATCH /api/task/{id}/status separately).
export function denormalizeTaskForUpdate(t) {
  if (!t) return t;
  return {
    title: t.title || '',
    description: t.description || '',
    // See note in denormalizeTaskForApi. `assigneeUsernames: []` means
    // "clear all assignees" per BE PATCH semantics; a non-empty array
    // replaces the full list.
    ...assigneeUsernamesToBody(t.assignee),
    priority: PRIORITY_UI_TO_API[t.priority] || 'MEDIUM',
    dueAt: stringToDueAt(t.due),
    labelIds: toInt64Array(t.labels && t.labels.length ? t.labels : (t.label ? [t.label] : [])),
    teamId: toInt64(t.team),
    ...customerIdsToBody(t.customer),
  };
}

// FE keeps `t.assignee` as an array of usernames. BE prefers the plural
// `assigneeUsernames` and takes element[0] as the primary. We tolerate
// scalar `t.assignee` for legacy data.
//   `null` / not-passed → BE PATCH semantics: no change (we don't send).
//   `[]`                → clear all assignees.
//   `["a","b"]`         → replace with [a, b], a is primary.
function assigneeUsernamesToBody(assignee) {
  if (assignee == null) {
    return { assigneeUsernames: [], assigneeUsername: null };
  }
  const arr = Array.isArray(assignee)
    ? assignee.filter((u) => u != null && u !== '')
    : (assignee === '' ? [] : [assignee]);
  return {
    assigneeUsernames: arr,
    assigneeUsername: arr[0] || null,
  };
}

// FE keeps `t.customer` as an array of contact ids (multi-select). BE now
// accepts the full list as `contactIds[]`; legacy `contactId` was retired
// in the same release so we deliberately don't send it anymore.
function customerIdsToBody(customer) {
  const arr = Array.isArray(customer) ? customer : (customer == null || customer === '' ? [] : [customer]);
  const ids = arr.map(toInt64).filter((n) => n != null);
  return { contactIds: ids };
}

function toInt64(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function toInt64Array(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map(toInt64).filter((n) => n != null);
}

// Build the typed TaskFieldValueRequest body based on the UI value + field type.
// 5 canonical types: text, number, date, singleselect, multiselect.
// Each maps to one typed slot on TaskFieldValueRequest.
export function buildFieldValuePayload(fieldType, value) {
  const body = {};
  const t = String(fieldType || 'text').toLowerCase();
  switch (t) {
    case 'text':
      body.valueText = value == null ? '' : String(value);
      break;
    case 'number':
      body.valueNumber = value === '' || value == null ? null : Number(value);
      break;
    case 'date':
      body.valueDate = stringToDueAt(value);
      break;
    case 'singleselect':
    case 'select':
      body.valueText = value == null ? '' : String(value);
      break;
    case 'multiselect':
      body.valueJson = Array.isArray(value)
        ? JSON.stringify(value)
        : value == null
          ? '[]'
          : (typeof value === 'string' ? value : JSON.stringify(value));
      break;
    default:
      body.valueJson = value == null
        ? ''
        : typeof value === 'string'
          ? value
          : JSON.stringify(value);
      break;
  }
  return body;
}
